import axios from 'axios';
import { logout, isTokenExpired } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

export const api = axios.create({ baseURL: API_URL });

export const setAuthToken = (token) => {
  if (token) {
    // Set token on the custom api instance
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    // Also set token globally on axios for components using axios directly
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
    delete axios.defaults.headers.common.Authorization;
  }
};

/*
 * Endpoints reachable without an account.
 *
 * '/api/register' does NOT cover the registration wizard: JavaScript's includes()
 * is a substring test, and '/api/registration-tokens/validate' does not contain
 * '/api/register' — "register" and "registration" diverge at the seventh letter.
 * So both wizard calls counted as protected, and the guard below logged the
 * visitor out and sent them to the login page before the request was ever sent.
 *
 * Every invited customer hit this, because an invited customer by definition has
 * no account yet. It only appeared to work for whoever was already signed in.
 */
const PUBLIC_ENDPOINTS = [
  '/api/login',
  '/api/register',
  '/api/registration-complete',
  '/api/registration-tokens/validate',
  '/api/forgot-password',
  '/api/password-reset',
  '/api/version',
  '/health'
];

const isPublicUrl = (url = '') => PUBLIC_ENDPOINTS.some((endpoint) => url.includes(endpoint));

// Request interceptor: check if token is expired BEFORE making request
const checkTokenBeforeRequest = (config) => {
  if (isPublicUrl(config.url)) return config;

  /*
   * Only tear down a session that actually exists. isTokenExpired() also returns
   * true when there is no token at all, so treating that as an expiry logged out
   * visitors who were never logged in and bounced them off public pages. If a
   * request genuinely needs auth, the server answers 401 and the response
   * interceptor handles it.
   */
  if (localStorage.getItem('token') && isTokenExpired()) {
    logout();
    return Promise.reject(new Error('Token expired'));
  }
  return config;
};

// Response interceptor: handle 401/403 from server (token invalid or expired)
const handleUnauthorized = (error) => {
  // Public endpoints report their own errors; a 401 from one must never log
  // anybody out or redirect them off the page they are standing on.
  if (isPublicUrl(error?.config?.url || '')) {
    return Promise.reject(error);
  }
  
  const status = error?.response?.status;
  const message = error?.response?.data?.message || '';
  
  // Handle both 401 (no token) and 403 (invalid/expired token)
  if (status === 401 || (status === 403 && message.toLowerCase().includes('token'))) {
    logout();
    return Promise.reject(error);
  }
  return Promise.reject(error);
};

// Apply interceptors to custom api instance
api.interceptors.request.use(checkTokenBeforeRequest, (error) => Promise.reject(error));
api.interceptors.response.use((response) => response, handleUnauthorized);

// Apply interceptors to global axios (for components using axios directly)
axios.interceptors.request.use(checkTokenBeforeRequest, (error) => Promise.reject(error));
axios.interceptors.response.use((response) => response, handleUnauthorized);
